import {
  Check,
  Eye,
  EyeOff,
  LockKeyhole,
  ShieldCheck,
  Sparkles,
  UserRound,
} from "lucide-react";

import {
  useState,
  type FormEvent,
} from "react";

import {
  ApiRequestError,
  login,
} from "./api";

import type {
  AuthenticatedUser,
} from "./types";

interface LoginScreenProperties {
  initialError?: string | null;

  onAuthenticated:
    (user: AuthenticatedUser) => void;
}

function LoginScreen({
  initialError,
  onAuthenticated,
}: LoginScreenProperties) {
  const [username, setUsername] =
    useState("planer");

  const [password, setPassword] =
    useState("");

  const [rememberMe, setRememberMe] =
    useState(true);

  const [showPassword, setShowPassword] =
    useState(false);

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const [error, setError] =
    useState<string | null>(
      initialError ?? null,
    );

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await login({
        username: username.trim(),
        password,
        rememberMe,
      });

      onAuthenticated(
        response.data.user,
      );
    } catch (requestError: unknown) {
      if (
        requestError instanceof ApiRequestError
        && requestError.status === 429
      ) {
        setError(
          "Слишком много попыток. Попробуй войти немного позже.",
        );
      } else {
        setError(
          requestError instanceof Error
            ? requestError.message
            : "Не удалось выполнить вход.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="login-screen">
      <div className="login-background-orb login-orb-one" />
      <div className="login-background-orb login-orb-two" />

      <section className="login-presentation">
        <div className="login-brand">
          <div className="login-brand-mark">
            P
          </div>

          <div>
            <strong>Planer</strong>
            <span>Персональный рабочий день</span>
          </div>
        </div>

        <div className="login-presentation-content">
          <div className="login-kicker">
            <Sparkles size={17} />
            <span>Собран вокруг твоего дня</span>
          </div>

          <h1>
            Спокойный контроль
            <br />
            над задачами и временем.
          </h1>

          <p>
            Проекты, расписание, фокус и
            фактическое время работы находятся
            в одном защищённом пространстве.
          </p>

          <div className="login-benefits">
            <div className="login-benefit">
              <span className="login-benefit-icon">
                <Check size={16} />
              </span>

              <span>
                Единый список задач и проектов
              </span>
            </div>

            <div className="login-benefit">
              <span className="login-benefit-icon">
                <Check size={16} />
              </span>

              <span>
                Таймер и сравнение план/факт
              </span>
            </div>

            <div className="login-benefit">
              <span className="login-benefit-icon">
                <Check size={16} />
              </span>

              <span>
                Защищённая личная сессия
              </span>
            </div>
          </div>
        </div>

        <div className="login-security-note">
          <ShieldCheck size={18} />

          <span>
            Соединение защищено HTTPS
          </span>
        </div>
      </section>

      <section className="login-form-side">
        <form
          className="login-card"
          onSubmit={(event) => {
            void handleSubmit(event);
          }}
        >
          <div className="login-card-icon">
            <LockKeyhole size={23} />
          </div>

          <div className="login-card-heading">
            <p>Добро пожаловать</p>
            <h2>Вход в Planer</h2>

            <span>
              Используй данные владельца,
              сохранённые на сервере.
            </span>
          </div>

          {error && (
            <div
              className="login-error"
              role="alert"
            >
              {error}
            </div>
          )}

          <label className="login-field">
            <span>Логин</span>

            <div className="login-input-shell">
              <UserRound size={18} />

              <input
                type="text"
                name="username"
                value={username}
                onChange={(event) => {
                  setUsername(
                    event.target.value,
                  );
                }}
                autoComplete="username"
                spellCheck={false}
                required
                minLength={3}
                maxLength={80}
                disabled={isSubmitting}
              />
            </div>
          </label>

          <label className="login-field">
            <span>Пароль</span>

            <div className="login-input-shell">
              <LockKeyhole size={18} />

              <input
                type={
                  showPassword
                    ? "text"
                    : "password"
                }
                name="password"
                value={password}
                onChange={(event) => {
                  setPassword(
                    event.target.value,
                  );
                }}
                autoComplete="current-password"
                required
                minLength={8}
                maxLength={256}
                autoFocus
                disabled={isSubmitting}
              />

              <button
                type="button"
                className="password-visibility"
                onClick={() => {
                  setShowPassword(
                    (currentValue) =>
                      !currentValue,
                  );
                }}
                aria-label={
                  showPassword
                    ? "Скрыть пароль"
                    : "Показать пароль"
                }
                disabled={isSubmitting}
              >
                {showPassword ? (
                  <EyeOff size={18} />
                ) : (
                  <Eye size={18} />
                )}
              </button>
            </div>
          </label>

          <label className="remember-option">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(event) => {
                setRememberMe(
                  event.target.checked,
                );
              }}
              disabled={isSubmitting}
            />

            <span className="remember-checkbox">
              <Check size={14} />
            </span>

            <span>
              <strong>Запомнить меня</strong>
              <small>
                Не запрашивать пароль 30 дней
              </small>
            </span>
          </label>

          <button
            type="submit"
            className="login-submit"
            disabled={
              isSubmitting
              || username.trim().length < 3
              || password.length < 8
            }
          >
            {isSubmitting
              ? "Входим…"
              : "Войти в Planer"}
          </button>

          <p className="login-cookie-note">
            Пароль не сохраняется в браузере.
            Вход поддерживается защищённой
            HttpOnly-cookie.
          </p>
        </form>
      </section>
    </main>
  );
}

export default LoginScreen;
